/**
 * Generic StreamingService provides utilities for making streaming HTTP requests (such as Server-Sent Events)
 * and managing active streaming connections. It includes an event emitter for handling streaming events,
 * and supports aborting active requests by request ID.
 *
 * Methods:
 *   - streamRequest({ url, method = 'POST', headers = {}, data = null, requestId = null }):
 *       Initiates a streaming HTTP request to the specified URL with the given method, headers, and data.
 *       Accepts:
 *         - url (string): The endpoint to send the request to.
 *         - method (string, optional): HTTP method (default: 'POST').
 *         - headers (object, optional): Additional headers for the request.
 *         - data (object|null, optional): Request payload (for POST/PUT).
 *         - requestId (string|null, optional): Unique identifier for the request, used for aborting.
 *       Returns:
 *         - Promise that resolves to an event emitter object.
 *           The event emitter provides:
 *             - on(eventType, callback): Listen for streaming events (e.g., 'data', 'end', 'error').
 *             - off(eventType, callback): Remove event listener.
 *             - abort(): Abort the active streaming request.
 */

class StreamingService {
  constructor() {
    this.activeRequests = new Map();
  }

  // eslint-disable-next-line class-methods-use-this
  createEventEmitter() {
    const eventListeners = new Map();

    return {
      on: (eventType, callback) => {
        if (!eventListeners.has(eventType)) {
          eventListeners.set(eventType, []);
        }
        eventListeners.get(eventType).push(callback);
      },
      off: (eventType, callback) => {
        const listeners = eventListeners.get(eventType) || [];
        const index = listeners.indexOf(callback);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      },
      emit: (eventType, data) => {
        const listeners = eventListeners.get(eventType) || [];
        listeners.forEach(callback => callback(data));
      },
    };
  }

  async streamRequest({
    url,
    method = 'POST',
    headers = {},
    data = null,
    requestId = null,
  }) {
    return new Promise(resolve => {
      const controller = new AbortController();
      const eventEmitter = this.createEventEmitter();

      // Add abort method to event emitter
      eventEmitter.abort = () => {
        controller.abort();
        if (requestId) {
          this.activeRequests.delete(requestId);
        }
      };

      // Store active request if ID provided
      if (requestId) {
        this.activeRequests.set(requestId, eventEmitter);
      }

      const defaultHeaders = {
        Accept: 'text/event-stream',
        'Cache-Control': 'no-cache',
        ...headers,
      };

      // Use fetch for streaming since axios doesn't handle SSE well
      fetch(url, {
        method,
        headers: defaultHeaders,
        body: data ? JSON.stringify(data) : null,
        signal: controller.signal,
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          const readStream = () => {
            return reader.read().then(({ done, value }) => {
              if (done) {
                eventEmitter.emit('end', { message: '' });
                if (requestId) {
                  this.activeRequests.delete(requestId);
                }
                return Promise.resolve();
              }

              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');

              buffer = lines.pop() || '';

              lines.forEach(line => {
                line = line.trim();

                if (line.startsWith('data: ')) {
                  try {
                    const jsonData = line.slice(6).trim();
                    if (jsonData === '[DONE]') {
                      eventEmitter.emit('end', { message: '' });
                    } else {
                      const parsedData = JSON.parse(jsonData);

                      // Emit based on 'event' field or 'type' field
                      if (parsedData.event) {
                        eventEmitter.emit(parsedData.event, parsedData);
                      } else if (parsedData.type) {
                        eventEmitter.emit(parsedData.type, parsedData);
                      }
                    }
                  } catch (error) {
                    eventEmitter.emit('error', {
                      type: 'parse_error',
                      message: 'Error parsing SSE data',
                      originalError: error,
                      data: line,
                    });
                  }
                } else if (line.startsWith('event: ')) {
                  const eventType = line.slice(7).trim();
                  eventEmitter.emit('event_type', { eventType });
                } else if (line.startsWith('id: ')) {
                  const eventId = line.slice(4).trim();
                  eventEmitter.emit('event_id', { eventId });
                }
              });

              return readStream();
            });
          };

          readStream().catch(streamError => {
            eventEmitter.emit('error', {
              type: 'stream_error',
              message: streamError.message,
              originalError: streamError,
            });
            if (requestId) {
              this.activeRequests.delete(requestId);
            }
          });
        })
        .catch(error => {
          if (error.name === 'AbortError') {
            eventEmitter.emit('aborted', { message: 'Request was aborted' });
          } else {
            eventEmitter.emit('error', {
              type: 'network_error',
              message: error.message,
              originalError: error,
            });
          }
          if (requestId) {
            this.activeRequests.delete(requestId);
          }
        });

      // Return the event emitter immediately to allow setting up listeners
      resolve(eventEmitter);
    });
  }

  abortRequest(requestId) {
    const request = this.activeRequests.get(requestId);
    if (request && request.abort) {
      request.abort();
    }
  }

  abortAllRequests() {
    this.activeRequests.forEach(request => {
      if (request.abort) {
        request.abort();
      }
    });
    this.activeRequests.clear();
  }
}

export default new StreamingService();
